import { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleButton, withStyles, MaterialSymbol } from 'vopay';
import { useToast } from '#shared/providers/Toast';
import styles from './CopyButton.module.scss';

const cn = withStyles(styles);

export type CopyButtonProps = {
  content: string | number;
  /**
   * `icon` (default): the bare copy glyph the shared tables use. `circle`: the round action button the
   * portals' credential strips use, beside their other round buttons.
   */
  variant?: 'icon' | 'circle';
  /** `icon` only. */
  color?: CSSProperties['color'];
  /** `icon` only. */
  size?: CSSProperties['fontSize'];
};

const PULSE_DURATION_MS = 200;

/** Copies `content` to the clipboard and confirms it with the standard toast. Every app's copy button. */
export default function CopyButton({
  content,
  variant = 'icon',
  color = 'hsla(216, 100%, 59%, 1)',
  size = '24px',
}: CopyButtonProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared' });
  const toast = useToast();
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (animating) {
      const timeout = setTimeout(() => {
        setAnimating(false);
      }, PULSE_DURATION_MS);

      return () => clearTimeout(timeout);
    }

    return () => {};
  }, [animating]);

  const label = t('Buttons.copy', { defaultValue: 'Copy' });

  const copy = () => {
    navigator.clipboard.writeText(content.toString());
    toast.success(t('copied', { defaultValue: 'Copied to clipboard' }));
  };

  if (variant === 'circle') {
    return <CircleButton icon="content_copy" colorScheme="action" aria-label={label} onClick={copy} />;
  }

  return (
    <button
      type="button"
      aria-label={label}
      style={{
        '--duration': `${PULSE_DURATION_MS}ms`,
      }}
      className={cn('copyButton', animating && 'animating')}
      onClick={() => {
        setAnimating(true);
        copy();
      }}
    >
      <MaterialSymbol icon="content_copy" size={size} color={color} className={cn('icon')} />
    </button>
  );
}
